// pages/AllComplaintTypes.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus, Search, Tag, Trash2, Edit3, Loader2,
    AlertCircle, Eye, X, Save, Calendar, Hash, ToggleLeft,
    CheckCircle2, XCircle, Download
} from 'lucide-react';
import { usePermissions } from '../../view/Permissions';

// ── API Config ─────────────────────────────────────────────────────────
const API_BASE = 'https://api.mobilink.tech/api/superAdmin';
const API_TOKEN = sessionStorage.getItem('adminToken') || sessionStorage.getItem('subAdminToken') || '';
const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${API_TOKEN}` };

// ── API Helpers ────────────────────────────────────────────────────────
const apiCall = async (url, options = {}) => {
    const res = await fetch(url, { ...options, headers });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Request failed (${res.status})`);
    }
    return res.json();
};

const fetchAllComplaintTypes = () =>
    apiCall(`${API_BASE}/complaintType/getall`, { method: 'GET' });

const fetchActiveComplaintTypes = () =>
    apiCall(`${API_BASE}/complaintType/getall-active`, { method: 'GET' });

const createComplaintType = (payload) =>
    apiCall(`${API_BASE}/complaintType/addd`, {
        method: 'POST',
        body: JSON.stringify(payload)
    });

// Note: backend only exposes a status-update route (no name field in body per the sample curl,
// but it's included here in case the API accepts it — extra keys are ignored server-side if unused).
const updateComplaintType = (id, payload) =>
    apiCall(`${API_BASE}/complaintType/updatede/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
    });

const deleteComplaintType = (id) =>
    apiCall(`${API_BASE}/complaintType/delete/${id}`, { method: 'DELETE' });

// ── Export Helper ──────────────────────────────────────────────────────
// Converts an array of complaint type objects into a downloadable CSV file.
// No external library needed — builds the CSV manually and triggers a
// browser download via a Blob + temporary anchor element.
const exportComplaintTypesToCSV = (rows) => {
    if (!rows || rows.length === 0) return;

    const headersRow = ['ID', 'Name', 'Status', 'Created At', 'Updated At'];

    const escapeCsvValue = (value) => {
        const str = value === null || value === undefined ? '' : String(value);
        // Wrap in quotes and escape any existing quotes if the value contains
        // commas, quotes, or newlines.
        if (/[",\n]/.test(str)) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const csvRows = rows.map((ct) =>
        [
            ct._id,
            ct.name,
            ct.status,
            ct.createdAt ? new Date(ct.createdAt).toISOString() : '',
            ct.updatedAt ? new Date(ct.updatedAt).toISOString() : ''
        ]
            .map(escapeCsvValue)
            .join(',')
    );

    const csvContent = [headersRow.join(','), ...csvRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.setAttribute('download', `complaint-types-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// ── Modal Wrapper ──────────────────────────────────────────────────────
const Modal = ({ isOpen, onClose, title, subtitle, children, maxWidth = 'max-w-lg' }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className={`w-full ${maxWidth} bg-white rounded-2xl shadow-2xl border border-orange-200 max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-orange-100 flex-shrink-0">
                    <div className="min-w-0">
                        <h2 className="text-base sm:text-lg font-bold text-gray-800">{title}</h2>
                        {subtitle && <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p>}
                    </div>
                    <button
                        onClick={onClose}
                        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="overflow-y-auto flex-1">{children}</div>
            </div>
        </div>
    );
};

// ── Detail Row ─────────────────────────────────────────────────────────
const DetailRow = ({ icon: Icon, label, value, valueClass = '' }) => (
    <div className="flex items-start gap-3 py-3 border-b border-orange-100 last:border-0">
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon size={14} style={{ color: '#FF7D38' }} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</p>
            <div className={`text-sm mt-0.5 break-all ${valueClass}`}>{value || '—'}</div>
        </div>
    </div>
);

// ── Add / Edit Modal ───────────────────────────────────────────────────
// There is no "get by id" endpoint for complaint types, so Edit is pre-filled
// directly from the row data already in the list (no extra fetch).
const AddEditComplaintTypeModal = ({ isOpen, onClose, complaintType, onSuccess }) => {
    const isEdit = !!complaintType;
    const [formData, setFormData] = useState({ name: '', status: 'active' });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            setFormData({ name: '', status: 'active' });
            setErrors({});
            setApiResponse(null);
            return;
        }

        if (isEdit && complaintType) {
            setFormData({
                name: complaintType.name || '',
                status: complaintType.status || 'active'
            });
        } else {
            setFormData({ name: '', status: 'active' });
        }
    }, [isOpen, complaintType, isEdit]);

    const validate = () => {
        const e = {};
        if (!formData.name.trim()) e.name = 'Complaint type name is required';
        else if (formData.name.trim().length < 2) e.name = 'Name must be at least 2 characters';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setApiResponse(null);

        try {
            const payload = {
                name: formData.name.trim(),
                status: formData.status
            };

            if (isEdit) {
                await updateComplaintType(complaintType._id, payload);
            } else {
                await createComplaintType(payload);
            }

            setApiResponse({ success: true, message: `Complaint type ${isEdit ? 'updated' : 'created'} successfully!` });

            setTimeout(() => {
                setLoading(false);
                onSuccess();
                onClose();
            }, 600);
        } catch (err) {
            setApiResponse({ success: false, error: err.message });
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? 'Edit Complaint Type' : 'Add New Complaint Type'}
            subtitle={isEdit ? `Update ${complaintType?.name || 'complaint type'} details` : 'Create a new complaint type'}
            maxWidth="max-w-md"
        >
            <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-5">
                {/* Name */}
                <div>
                    <label className="block text-sm font-medium mb-1.5 text-gray-600">
                        Complaint Type Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className={`w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl bg-orange-50 border ${errors.name ? 'border-red-300 focus:border-red-400' : 'border-orange-200 focus:border-orange-400'
                                } focus:ring-2 focus:ring-orange-200 outline-none`}
                            placeholder="e.g., Login Issue, Payment"
                            disabled={loading}
                            autoFocus
                        />
                    </div>
                    {errors.name && (
                        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                            <XCircle size={12} /> {errors.name}
                        </p>
                    )}
                </div>

                {/* Status */}
                <div>
                    <label className="block text-sm font-medium mb-1.5 text-gray-600">Status</label>
                    <div className="relative">
                        <ToggleLeft size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl appearance-none bg-orange-50 border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none"
                            disabled={loading}
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                </div>

                {/* API Response */}
                {apiResponse && (
                    <div
                        className={`p-3 rounded-xl flex items-start gap-2 ${apiResponse.success
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-red-50 border border-red-200'
                            }`}
                    >
                        {apiResponse.success ? (
                            <CheckCircle2 size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                            <XCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <p className={`text-sm ${apiResponse.success ? 'text-green-700' : 'text-red-700'}`}>
                            {apiResponse.success ? apiResponse.message : apiResponse.error}
                        </p>
                    </div>
                )}

                {/* Footer Actions */}
                <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium rounded-xl text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl disabled:opacity-70 bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm transition-all"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={14} className="animate-spin" /> Saving...
                            </>
                        ) : (
                            <>
                                <Save size={14} /> {isEdit ? 'Update' : 'Create'} Complaint Type
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

// ── View Details Modal ─────────────────────────────────────────────────
// No get-by-id endpoint available, so this just renders the row data passed in.
const ViewComplaintTypeModal = ({ isOpen, onClose, complaintType }) => {
    if (!isOpen || !complaintType) return null;

    const formatDate = (d) =>
        d ? new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Complaint Type Details"
            subtitle={complaintType.name}
            maxWidth="max-w-md"
        >
            <div className="p-4 sm:p-5">
                <div className="space-y-1">
                    <DetailRow icon={Hash} label="Complaint Type ID" value={complaintType._id} valueClass="text-xs font-mono text-gray-500" />
                    <DetailRow
                        icon={Tag}
                        label="Name"
                        value={complaintType.name}
                        valueClass="font-semibold text-gray-800"
                    />
                    <DetailRow
                        icon={ToggleLeft}
                        label="Status"
                        value={
                            <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${complaintType.status === 'active'
                                    ? 'bg-green-50 text-green-600 border-green-200'
                                    : 'bg-gray-50 text-gray-500 border-gray-200'
                                    }`}
                            >
                                {complaintType.status}
                            </span>
                        }
                    />
                    <DetailRow
                        icon={Calendar}
                        label="Created"
                        value={formatDate(complaintType.createdAt)}
                        valueClass="text-xs text-gray-600"
                    />
                    <DetailRow
                        icon={Calendar}
                        label="Last Updated"
                        value={formatDate(complaintType.updatedAt)}
                        valueClass="text-xs text-gray-600"
                    />
                </div>

                <div className="mt-5 pt-4 border-t border-orange-100 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium rounded-xl bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ── Delete Confirmation Modal ──────────────────────────────────────────
const DeleteConfirmModal = ({ isOpen, onClose, complaintType, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleDelete = async () => {
        if (!complaintType?._id) return;
        setLoading(true);
        setError(null);
        try {
            await deleteComplaintType(complaintType._id);
            onSuccess(complaintType._id);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Delete Complaint Type"
            subtitle="This action cannot be undone"
            maxWidth="max-w-sm"
        >
            <div className="p-4 sm:p-5 space-y-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                        <Trash2 size={18} className="text-red-500" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm text-gray-700 leading-relaxed">
                            Are you sure you want to delete{' '}
                            <span className="font-semibold text-gray-900">
                                "{complaintType?.name}"
                            </span>
                            ? This action cannot be undone and all associated data will be permanently removed.
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
                        <XCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                )}

                <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-2">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium rounded-xl text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={loading}
                        className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl disabled:opacity-70 bg-gradient-to-r from-red-500 to-red-600 hover:shadow-red-500/30 shadow-sm transition-all"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={14} className="animate-spin" /> Deleting...
                            </>
                        ) : (
                            <>
                                <Trash2 size={14} /> Delete Complaint Type
                            </>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ── Main Component ─────────────────────────────────────────────────────
const AllComplaintTypes = () => {
    const [complaintTypes, setComplaintTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all | active
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);

    const {
        canView,
        canAdd,
        canEdit,
        canDelete,
        canDownload,
        canExport
    } = usePermissions();

    // Modal states
    const [addEditModal, setAddEditModal] = useState({ isOpen: false, complaintType: null });
    const [viewModal, setViewModal] = useState({ isOpen: false, complaintType: null });
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, complaintType: null });

    const fetchComplaintTypes = useCallback(async (filter = statusFilter) => {
        setLoading(true);
        setError(null);
        try {
            const data = filter === 'active'
                ? await fetchActiveComplaintTypes()
                : await fetchAllComplaintTypes();
            setComplaintTypes(data.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchComplaintTypes(statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const filteredComplaintTypes = complaintTypes.filter(
        (c) =>
            c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.status?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Modal handlers
    const openAddModal = () => setAddEditModal({ isOpen: true, complaintType: null });
    const openEditModal = (ct) => setAddEditModal({ isOpen: true, complaintType: ct });
    const closeAddEditModal = () => setAddEditModal({ isOpen: false, complaintType: null });

    const openViewModal = (ct) => setViewModal({ isOpen: true, complaintType: ct });
    const closeViewModal = () => setViewModal({ isOpen: false, complaintType: null });

    const openDeleteModal = (ct) => setDeleteModal({ isOpen: true, complaintType: ct });
    const closeDeleteModal = () => setDeleteModal({ isOpen: false, complaintType: null });

    const handleSaveSuccess = () => fetchComplaintTypes(statusFilter);
    const handleDeleteSuccess = (deletedId) =>
        setComplaintTypes((prev) => prev.filter((c) => c._id !== deletedId));

    // Exports whatever is currently visible (respects search + status filter)
    // so the downloaded file matches what the admin is looking at.
    const handleExport = () => {
        if (exporting || filteredComplaintTypes.length === 0) return;
        setExporting(true);
        try {
            exportComplaintTypesToCSV(filteredComplaintTypes);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="min-h-screen space-y-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 sm:p-5 rounded-2xl border border-orange-200 shadow-sm">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                        <Tag size={20} style={{ color: '#FF7D38' }} />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg sm:text-xl font-bold text-gray-800 truncate">
                            Complaint Type Management
                        </h1>
                        <p className="text-xs sm:text-sm text-gray-500">
                            {complaintTypes.length} complaint type{complaintTypes.length !== 1 ? 's' : ''} configured
                        </p>
                    </div>
                </div>
                <div className="flex flex-col xs:flex-row sm:flex-row gap-2 w-full sm:w-auto">
                    {canExport('Complaint Types') && (
                        <button
                            onClick={handleExport}
                            disabled={exporting || filteredComplaintTypes.length === 0}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        >
                            {exporting ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Download size={16} />
                            )}
                            Export CSV
                        </button>
                    )}
                    {canAdd('Complaint Types') && (
                        <button
                            onClick={openAddModal}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm transition-all whitespace-nowrap w-full sm:w-auto"
                        >
                            <Plus size={16} /> Add New Complaint Type
                        </button>
                    )}
                </div>
            </div>

            {/* Search + Status Filter */}
            <div className="bg-white p-4 rounded-2xl border border-orange-200 shadow-sm flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1 sm:max-w-md">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search complaint types by name or status..."
                        className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl bg-orange-50 border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none"
                    />
                </div>
                <div className="flex gap-2">
                    {[
                        { key: 'all', label: 'All' },
                        { key: 'active', label: 'Active Only' }
                    ].map((opt) => (
                        <button
                            key={opt.key}
                            onClick={() => setStatusFilter(opt.key)}
                            className={`flex-1 sm:flex-none px-3.5 py-2 text-xs font-medium rounded-xl border transition-colors ${statusFilter === opt.key
                                ? 'bg-orange-500 text-white border-orange-500'
                                : 'bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100'
                                }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Error State */}
            {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-700 flex-1">{error}</p>
                    </div>
                    <button
                        onClick={() => fetchComplaintTypes(statusFilter)}
                        className="text-xs font-medium text-red-600 hover:underline self-start sm:self-auto"
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="flex flex-col items-center justify-center py-20">
                    <Loader2 size={36} className="animate-spin text-orange-500 mb-3" />
                    <p className="text-sm text-gray-500">Loading complaint types...</p>
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && filteredComplaintTypes.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 sm:py-20 px-4 bg-white rounded-2xl border border-orange-200 text-center">
                    <Tag size={48} className="text-gray-300 mb-4" />
                    <p className="text-gray-500 font-medium">
                        {searchTerm ? 'No complaint types match your search' : 'No complaint types found'}
                    </p>
                    {!searchTerm && canAdd('Complaint Types') && (
                        <button
                            onClick={openAddModal}
                            className="mt-4 text-sm text-orange-500 font-medium hover:underline"
                        >
                            Create your first complaint type →
                        </button>
                    )}
                </div>
            )}

            {/* Complaint Types Grid */}
            {!loading && !error && filteredComplaintTypes.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredComplaintTypes.map((ct) => (
                        <div
                            key={ct._id}
                            className="bg-white rounded-2xl border border-orange-200 p-4 sm:p-5 hover:shadow-md transition-shadow flex flex-col gap-4"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                                        <Tag size={18} style={{ color: '#FF7D38' }} />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-sm font-bold text-gray-800 truncate">
                                            {ct.name}
                                        </h3>
                                        <p className="text-[10px] text-gray-400 truncate">
                                            ID: {ct._id.slice(-8)}
                                        </p>
                                    </div>
                                </div>
                                <span
                                    className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full border font-medium ${ct.status === 'active'
                                        ? 'bg-green-50 text-green-600 border-green-200'
                                        : 'bg-gray-50 text-gray-500 border-gray-200'
                                        }`}
                                >
                                    {ct.status}
                                </span>
                            </div>

                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                                <div className="flex items-center gap-1.5">
                                    <Tag size={12} />
                                    <span>Complaint Type</span>
                                </div>
                                <span className="hidden xs:inline text-gray-300">|</span>
                                <span>Created {formatDate(ct.createdAt)}</span>
                            </div>

                            <div className="flex items-center gap-2 pt-3 border-t border-orange-100 mt-auto">
                                {canEdit('Complaint Types') && (
                                    <button
                                        onClick={() => openEditModal(ct)}
                                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 transition-colors"
                                    >
                                        <Edit3 size={13} /> Edit
                                    </button>
                                )}
                                {canView('Complaint Types') && (
                                    <button
                                        onClick={() => openViewModal(ct)}
                                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors"
                                    >
                                        <Eye size={13} /> View
                                    </button>
                                )}
                                {canDelete('Complaint Types') && (
                                    <button
                                        onClick={() => openDeleteModal(ct)}
                                        className="flex items-center justify-center w-9 h-9 rounded-lg bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 transition-colors flex-shrink-0"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Modals ────────────────────────────────────────────────── */}
            <AddEditComplaintTypeModal
                isOpen={addEditModal.isOpen}
                onClose={closeAddEditModal}
                complaintType={addEditModal.complaintType}
                onSuccess={handleSaveSuccess}
            />

            <ViewComplaintTypeModal
                isOpen={viewModal.isOpen}
                onClose={closeViewModal}
                complaintType={viewModal.complaintType}
            />

            <DeleteConfirmModal
                isOpen={deleteModal.isOpen}
                onClose={closeDeleteModal}
                complaintType={deleteModal.complaintType}
                onSuccess={handleDeleteSuccess}
            />
        </div>
    );
};

export default AllComplaintTypes;